import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Upload } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';

const Categories = () => {
    const [categories, setCategories] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/categories');
            if (response.data.categories) {
                setCategories(response.data.categories);
            } else {
                message.error('Failed to fetch categories.');
                setCategories([]);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch categories.');
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const showModal = (category = null) => {
        if (category) {
            setEditingCategory(category);
            form.setFieldsValue(category);
        } else {
            setEditingCategory(null);
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleOk = async (values) => {
        try {
            let response;
            if (editingCategory) {
                response = await axiosInstance.put(`/categories/${editingCategory.id}`, values);
                message.success('Category updated successfully');
            } else {
                response = await axiosInstance.post('/categories', values);
                message.success('Category added successfully');
            }
            fetchCategories();
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            message.error('Failed to save category.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    // Generate proper Excel template
    const downloadTemplate = () => {
        try {
            // Template with only Name column
            const templateData = [
                ['Name'], // Header
                ['Electronics'], // Sample data
                ['Clothing'],
                ['Food & Beverages']
            ];

            // Create CSV content
            const csvContent = templateData.map(row =>
                row.map(field => {
                    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
                        return `"${field.replace(/"/g, '""')}"`;
                    }
                    return field;
                }).join(',')
            ).join('\r\n');

            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvContent], {
                type: 'text/csv;charset=utf-8'
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', 'categories_template.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success('CSV template downloaded successfully');
        } catch (error) {
            console.error('Download error:', error);
            message.error('Failed to download template');
        }
    };


    // Alternative: Create actual Excel file using SheetJS (if available)
    const downloadExcelTemplate = () => {
        try {
            if (typeof window.XLSX !== 'undefined') {
                const wb = window.XLSX.utils.book_new();
                const ws = window.XLSX.utils.aoa_to_sheet([
                    ['Name'],        // Header
                    ['Electronics'], // Sample data
                    ['Clothing'],
                    ['Food & Beverages']
                ]);

                window.XLSX.utils.book_append_sheet(wb, ws, 'Categories');
                window.XLSX.writeFile(wb, 'categories_template.xlsx');
                message.success('Excel template downloaded successfully');
            } else {
                // Fallback to CSV
                downloadTemplate();
            }
        } catch (error) {
            console.error('Excel download error:', error);
            downloadTemplate(); // Fallback to CSV
        }
    };


    // Handle file upload
    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.error('Please select an Excel file to upload');
            return;
        }

        const formData = new FormData();
        formData.append('file', fileList[0]);

        setUploading(true);
        try {
            const response = await axiosInstance.post('/categories/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.message) {
                message.success(response.data.message);
                setImportModalVisible(false);
                setFileList([]);
                fetchCategories();
            }

            // Handle import errors if any
            if (response.data.errors && response.data.errors.length > 0) {
                message.warning(`Import completed with ${response.data.errors.length} errors`);
                console.log('Import errors:', response.data.errors);

                // Show detailed errors
                Modal.info({
                    title: 'Import Errors',
                    content: (
                        <div>
                            <p>The following errors occurred during import:</p>
                            <ul>
                                {response.data.errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    ),
                    width: 600,
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                message.error('Validation errors: ' + errorMessages.join(', '));
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Failed to upload Excel file');
            }
        } finally {
            setUploading(false);
        }
    };

    // Upload props - Excel and CSV
    const uploadProps = {
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: (file) => {
            // Check file extension
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const allowedExtensions = ['xlsx', 'xls', 'csv'];

            if (!allowedExtensions.includes(fileExtension)) {
                message.error('You can only upload Excel files (.xlsx, .xls) or CSV files!');
                return false;
            }

            // Check file size (5MB max)
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                message.error('File must be smaller than 5MB!');
                return false;
            }

            setFileList([file]);
            return false; // Prevent auto upload
        },
        fileList,
        accept: '.xlsx,.xls,.csv'
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => date ? new Date(date).toLocaleDateString() : '-',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, category) => (
                <Button onClick={() => showModal(category)}>Edit</Button>
            ),
        },
    ];

    return (
        <div>
            <div style={{ textAlign: 'right', marginBottom: 16, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button
                    icon={<DownloadOutlined />}
                    onClick={downloadExcelTemplate}
                >
                    Download Excel Template
                </Button>
                <Button
                    icon={<UploadOutlined />}
                    onClick={() => setImportModalVisible(true)}
                >
                    Import Categories
                </Button>
                <Button type="primary" onClick={() => showModal()}>
                    Add Category
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={Array.isArray(categories) ? categories : []}
                rowKey="id"
                pagination={{ pageSize: 25 }}
                scroll={{ x: 'max-content' }}
                loading={loading}
            />

            {/* Add/Edit Category Modal */}
            <Modal
                title={editingCategory ? 'Edit Category' : 'Add New Category'}
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                okText={editingCategory ? 'Update' : 'Add'}
                cancelText="Cancel"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleOk}
                >
                    <Form.Item
                        name="name"
                        label="Category Name"
                        rules={[{ required: true, message: 'Please input the category name!' }]}
                    >
                        <Input placeholder="Enter category name" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Import Categories Modal */}
            <Modal
                title="Import Categories"
                visible={importModalVisible}
                onOk={handleUpload}
                onCancel={() => {
                    setImportModalVisible(false);
                    setFileList([]);
                }}
                okText="Upload"
                cancelText="Cancel"
                confirmLoading={uploading}
            >
                <div style={{ marginBottom: 16 }}>
                    <p>Upload an Excel file (.xlsx, .xls) or CSV file to import multiple categories at once.</p>
                    <Button
                        type="link"
                        onClick={downloadExcelTemplate}
                        style={{ padding: 0 }}
                    >
                        Download template file
                    </Button>
                </div>

                <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>

                <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
                    <p><strong>Supported formats:</strong> XLSX, XLS, CSV</p>
                    <p><strong>Max file size:</strong> 5MB</p>
                    <p><strong>Required column:</strong> Name</p>
                    <p><strong>Optional columns:</strong> Description, Color Code</p>
                    <p><strong>Note:</strong> Delete sample data rows before adding your own data</p>
                </div>
            </Modal>
        </div>
    );
};

export default Categories;